import './styles.scss';
import {
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { FiEdit2 } from '@react-icons/all-files/fi/FiEdit2';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import React, { useState, useCallback, HTMLAttributes } from 'react';
import { useSelector } from 'react-redux';
import FavoriteCreateListModal from '../FavoriteListCreateModal';
import { Product } from '~domain/product/Product';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import {
  makeSelectFavoriteProductId,
  makeSelectFavoriteProductListIdByProduct,
  selectFavoriteProductLists,
  selectIsFavoriteProductListLoading,
} from '~redux/favorite/reducers/product';
import {
  removeFavoriteProduct as removeFavoriteProductThunk,
  addFavoriteProduct as addFavoriteProductThunk,
} from '~redux/favoriteproduct/thunk';
import { useThunk } from '~utils/hooks';

export type FavoriteListDialogProps = {
  product: Product;
  togglerComponent?: React.ComponentType<HTMLAttributes<HTMLButtonElement>>;
};

const FavoriteListDialog: React.FC<FavoriteListDialogProps> = ({
  product,
  togglerComponent: TogglerComponent,
}) => {
  const user = useSelector(authUserSelector);
  const [addFavoriteProduct] = useThunk(addFavoriteProductThunk);
  const [removeFavoriteProduct] = useThunk(removeFavoriteProductThunk);

  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback((event?) => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);
  const [createBoardIsOpen, setCreateBoardIsOpen] = useState(false);
  const favoriteLists = useSelector(selectFavoriteProductLists);
  const isListLoading = useSelector(selectIsFavoriteProductListLoading);
  const savedListId = useSelector(
    makeSelectFavoriteProductListIdByProduct(product.id)
  );
  const favoriteProductId = useSelector(
    makeSelectFavoriteProductId(product.id)
  );

  const handleListClick = useCallback(
    (listId: string) => {
      if (favoriteProductId) {
        removeFavoriteProduct(favoriteProductId);
      }
      if (savedListId !== listId) {
        addFavoriteProduct(user?.attributes.sub!, listId, product);
      }
      toggle();
    },
    [user, savedListId, favoriteProductId]
  );

  const handleCreateList = useCallback(() => {
    setCreateBoardIsOpen(true);
  }, []);

  if (isListLoading) {
    return null;
  }

  return (
    <>
      {TogglerComponent && <TogglerComponent onClick={toggle} />}
      {!TogglerComponent && (
        <button onClick={toggle} className="border-0 bg-transparent p-0">
          <div
            className="bg-white rounded-circle d-flex justify-content-center align-items-center"
            style={{ width: 32, height: 32 }}
          >
            <FiEdit2 size={20} color="gray" fill="gray" />
          </div>
        </button>
      )}
      <Dialog open={isOpen} onClose={toggle}>
        <DialogTitle>保存するリストを選択してください</DialogTitle>
        <List className="pt-0">
          {favoriteLists.map((list) => (
            <ListItem
              button
              key={list.listId}
              onClick={() => handleListClick(list.listId)}
            >
              <ListItemText>
                <div className="d-flex justify-content-between">
                  <div>{list.listName}</div>
                  {savedListId === list.listId && (
                    <div className="text-black-50">
                      <FiCheck className="mr-2" />
                      保存済み
                    </div>
                  )}
                </div>
              </ListItemText>
            </ListItem>
          ))}
          <ListItem button onClick={() => handleCreateList()}>
            <ListItemText>
              <FiPlus className="mr-2" />
              新しいリストを作成
            </ListItemText>
          </ListItem>
        </List>
        <FavoriteCreateListModal
          isOpen={createBoardIsOpen}
          setIsOpen={setCreateBoardIsOpen}
          accountId={user?.attributes.sub}
        />
      </Dialog>
    </>
  );
};

export default FavoriteListDialog;
